import React, { createContext, useEffect, useMemo, useState } from "react";
import { useRoom } from "./hooks/use-room";
import { LiveKitContextType } from "./types";
import { api } from "core/utility";
import config from "core/config";
import { retryFunctionNTimes } from "./utils";
import { logger } from "library/core/utility";
import { logToGraylog } from "core/stores";

/**
 * Livekit context provider
 * May move to submodule to be used with aff and cams.
 */
export const LiveKitContext = createContext<LiveKitContextType | null>(null);

interface LiveKitProviderProps {
  children: React.ReactNode;
  room: string;
  userName: string;
  userId: string;
}

export const LiveKitProvider: React.FC<LiveKitProviderProps> = ({
  children,
  room,
  userName,
  userId,
}) => {
  const {
    room: _room,
    roomName,
    hostTracks,
    participants,
    isConnecting: isRoomConnecting,
    connect: connectRoom,
    disconnect: disconnectRoom,
    setRoomName,
  } = useRoom();

  const [error, setError] = useState<Error | null>(null);
  const [isConnecting, setIsConnecting] = useState<boolean>(false);

  const getTokenAndConnect = async () => {
    const playbackDataResponse = await api
      .getLivekitPlaybackToken(userId, userName, room)
      .get();
    const playbackData = playbackDataResponse.data;
    logToGraylog("[Livekit]", "getTokenAndConnect", playbackData);
    await connectRoom(config.livekitWsServerUrl, playbackData.token);
  };

  /**
   * Connect LiveKit Room and start publisher. Viewers will connect but not publish.
   */
  const connect = async () => {
    try {
      setIsConnecting(true);
      await retryFunctionNTimes(getTokenAndConnect, 3);
      logger.log(`Livekit connected to room ${room}`);
      logToGraylog("[Livekit]", "Connected to room", { room, userName, userId });
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to connect"));
      disconnect();
    } finally {
      setIsConnecting(false);
    }
  };

  /**
   * Disconnect from LiveKit Room and stop publisher
   */
  const disconnect = async () => {
    try {
      // await stopPublishing();
      await disconnectRoom();
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to disconnect"));
    }
  };

  useEffect(() => {
    if (room) {
      setRoomName(room);
    }
  }, []);

  const value: LiveKitContextType = useMemo(() => {
    return {
      room: _room,
      hostTracks: hostTracks,
      participants,
      isConnecting: isConnecting || isRoomConnecting,
      error,
      roomName,
      connect,
      disconnect,
      setRoomName,
    };
  }, [
    _room,
    hostTracks,
    participants,
    isConnecting,
    isRoomConnecting,
    error,
    roomName,
  ]);

  return (
    <LiveKitContext.Provider value={value}>{children}</LiveKitContext.Provider>
  );
};
